/**
 * FormFillDialog
 *
 * Phase 7: User-Facing Custom Form Library
 *
 * Dialog for filling a custom form with job data.
 * Shows preview of what data will be filled and allows overrides.
 */

import { useState, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Download,
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Edit2,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCustomForm } from '@/hooks/useCustomForms';
import { useToast } from '@/hooks/use-toast';

interface FormFillDialogProps {
  open: boolean;
  onClose: () => void;
  formId: string;
  jobId?: string;
  onFilled?: (pdfBase64: string, formName: string) => void;
}

interface FillResult {
  success: boolean;
  pdfBase64: string;
  filledFields: Array<{
    field: string;
    value: string;
    source: string;
    semanticId: string | null;
    error?: string;
  }>;
  stats: {
    total: number;
    filled: number;
    autoFilled: number;
    overrides: number;
  };
}

export function FormFillDialog({
  open,
  onClose,
  formId,
  jobId,
  onFilled,
}: FormFillDialogProps) {
  const { toast } = useToast();
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [flatten, setFlatten] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');
  const [fillResult, setFillResult] = useState<FillResult | null>(null);

  // Fetch form details
  const { data: form, isLoading: formLoading } = useCustomForm(formId);

  // Fill form mutation
  const fillMutation = useMutation({
    mutationFn: async () => {
      const fillCustomForm = httpsCallable<
        { formId: string; jobId?: string; overrides: Record<string, string>; flatten: boolean },
        FillResult
      >(functions, 'fillCustomForm');

      const result = await fillCustomForm({
        formId,
        jobId,
        overrides,
        flatten,
      });

      return result.data;
    },
    onSuccess: (data) => {
      setFillResult(data);
      setActiveTab('result');
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to fill form',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Group fields by section
  const fieldsBySection = useMemo(() => {
    if (!form?.fields) return {};

    const sections: Record<string, typeof form.fields> = {};
    const sectionOrder = form.sections?.map(s => s.id) || [];

    for (const field of form.fields) {
      const sectionId = field.section || 'other';
      if (!sections[sectionId]) {
        sections[sectionId] = [];
      }
      sections[sectionId].push(field);
    }

    // Sort sections by order
    const sortedSections: Record<string, typeof form.fields> = {};
    for (const sectionId of sectionOrder) {
      if (sections[sectionId]) {
        sortedSections[sectionId] = sections[sectionId];
        delete sections[sectionId];
      }
    }
    // Add remaining sections
    Object.assign(sortedSections, sections);

    return sortedSections;
  }, [form]);

  const getSectionName = (sectionId: string) => {
    const section = form?.sections?.find(s => s.id === sectionId);
    if (section?.name) return section.name;
    return sectionId
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleOverrideChange = (fieldName: string, value: string) => {
    setOverrides(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleClearOverride = (fieldName: string) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleFill = () => {
    fillMutation.mutate();
  };

  const handleDownload = () => {
    if (!fillResult?.pdfBase64 || !form) return;

    // Convert base64 to blob
    const byteCharacters = atob(fillResult.pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    // Create download link
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form.form_name.replace(/\s+/g, '_')}_filled.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: 'PDF Downloaded',
      description: 'Your filled form has been downloaded.',
    });

    onFilled?.(fillResult.pdfBase64, form.form_name);
  };

  const handleClose = () => {
    setOverrides({});
    setFillResult(null);
    setActiveTab('preview');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {formLoading ? <Skeleton className="h-6 w-48" /> : form?.form_name}
          </DialogTitle>
          <DialogDescription>
            {jobId
              ? 'Fill this form with job data'
              : 'Fill this form with your contractor data'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 min-h-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="preview">Preview Fields</TabsTrigger>
            <TabsTrigger value="result" disabled={!fillResult}>
              Result
              {fillResult && (
                <Badge variant="secondary" className="ml-2">
                  {fillResult.stats.filled}/{fillResult.stats.total}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="flex-1 min-h-0 mt-4">
            {formLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
              </div>
            ) : (
              <ScrollArea className="h-[400px] pr-4">
                <Accordion type="multiple" defaultValue={Object.keys(fieldsBySection)} className="space-y-2">
                  {Object.entries(fieldsBySection).map(([sectionId, fields]) => (
                    <AccordionItem key={sectionId} value={sectionId} className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{getSectionName(sectionId)}</span>
                          <Badge variant="outline" className="text-xs">
                            {fields.length} fields
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 pt-2">
                          {fields.map((field) => (
                            <div key={field.pdfField} className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <Label
                                  htmlFor={field.pdfField}
                                  className="text-sm flex items-center gap-2"
                                >
                                  {field.label || field.pdfField}
                                  {field.semanticId && field.semanticId !== 'unknown' && (
                                    <Badge variant="secondary" className="text-xs font-normal">
                                      <Sparkles className="h-3 w-3 mr-1" />
                                      Auto-fill
                                    </Badge>
                                  )}
                                </Label>
                                {overrides[field.pdfField] !== undefined && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => handleClearOverride(field.pdfField)}
                                  >
                                    <RotateCcw className="h-3 w-3 mr-1" />
                                    Reset
                                  </Button>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Input
                                  id={field.pdfField}
                                  value={overrides[field.pdfField] ?? ''}
                                  onChange={(e) => handleOverrideChange(field.pdfField, e.target.value)}
                                  placeholder={
                                    field.semanticId && field.semanticId !== 'unknown'
                                      ? `Auto-filled from ${field.semanticId.replace(/_/g, ' ')}`
                                      : 'Enter value...'
                                  }
                                  className={cn(
                                    overrides[field.pdfField] !== undefined && 'border-primary'
                                  )}
                                />
                                {overrides[field.pdfField] !== undefined && (
                                  <Edit2 className="h-4 w-4 text-primary flex-shrink-0" />
                                )}
                              </div>
                              {field.semanticId && field.semanticId !== 'unknown' && (
                                <p className="text-xs text-muted-foreground">
                                  Maps to: {field.semanticId.replace(/_/g, ' ')}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                {/* Options */}
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="flatten"
                      checked={flatten}
                      onCheckedChange={(checked) => setFlatten(checked as boolean)}
                    />
                    <Label htmlFor="flatten" className="text-sm">
                      Lock fields after filling (flatten PDF)
                    </Label>
                  </div>
                </div>
              </ScrollArea>
            )}
          </TabsContent>

          <TabsContent value="result" className="flex-1 min-h-0 mt-4">
            {fillResult && (
              <ScrollArea className="h-[400px] pr-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-muted rounded-lg text-center">
                    <p className="text-2xl font-bold">{fillResult.stats.filled}</p>
                    <p className="text-xs text-muted-foreground">Fields Filled</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {fillResult.stats.autoFilled}
                    </p>
                    <p className="text-xs text-muted-foreground">Auto-filled</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {fillResult.stats.overrides}
                    </p>
                    <p className="text-xs text-muted-foreground">Overrides</p>
                  </div>
                </div>

                {/* Filled Fields */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Filled Fields</h4>
                  <div className="space-y-1">
                    {fillResult.filledFields
                      .filter(f => f.value)
                      .map((field) => (
                        <div
                          key={field.field}
                          className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm"
                        >
                          <span className="text-muted-foreground truncate flex-1">
                            {field.field}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate max-w-[200px]">
                              {field.value}
                            </span>
                            {field.source.startsWith('semantic:') ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                            ) : field.source === 'override' ? (
                              <Edit2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                            ) : null}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Empty Fields */}
                {fillResult.filledFields.filter(f => !f.value).length > 0 && (
                  <div className="mt-4 space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground">
                      Empty Fields ({fillResult.filledFields.filter(f => !f.value).length})
                    </h4>
                    <div className="space-y-1">
                      {fillResult.filledFields
                        .filter(f => !f.value)
                        .slice(0, 10)
                        .map((field) => (
                          <div
                            key={field.field}
                            className="flex items-center justify-between p-2 bg-amber-50/50 rounded text-sm"
                          >
                            <span className="text-muted-foreground truncate">
                              {field.field}
                            </span>
                            <AlertCircle className="h-4 w-4 text-amber-500" />
                          </div>
                        ))}
                      {fillResult.filledFields.filter(f => !f.value).length > 10 && (
                        <p className="text-xs text-muted-foreground text-center py-2">
                          + {fillResult.filledFields.filter(f => !f.value).length - 10} more empty fields
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {fillResult ? (
            <Button onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          ) : (
            <Button onClick={handleFill} disabled={fillMutation.isPending || formLoading}>
              {fillMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Filling...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Fill Form
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
